import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

const zodUpdateCompanyInvoiceCheckDelaySchema = z
  .object({
    delayedDays: z.coerce.number().nonnegative().max(30).prefault(0),
    onlyIncludeWorkdays: z.boolean().prefault(false),
  })
  .transform((value) =>
    withoutObjectKeysWhereValueIs(value, [null, undefined, '']),
  );

type CompanyInvoiceCheckDelayPayload = z.infer<
  typeof zodUpdateCompanyInvoiceCheckDelaySchema
>;

type UpdateCompanyInvoiceCheckDelayArguments = {
  companyId: UUID;
  invoiceCheckDelay: CompanyInvoiceCheckDelayPayload;
};

/**
 * Updates a company's invoice check delay settings.
 *
 * @param companyId - The ID of the company to update.
 * @param invoiceCheckDelay - The invoice check delay settings to update.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/set_invoice_check_delay_vadmin_company__company_id__invoice_check_delay_post
 */
// fallow-ignore-next-line unused-export
export const updateCompanyInvoiceCheckDelay = async (
  companyId: UUID,
  invoiceCheckDelay: CompanyInvoiceCheckDelayPayload,
) => {
  try {
    return await vestigasApi
      .post(ENDPOINT.COMPANY.INVOICE_CHECK_DELAY.UPDATE(companyId), {
        json: invoiceCheckDelay,
        validate: {
          body: zodUpdateCompanyInvoiceCheckDelaySchema,
          response: zodSuccessResponseSchema,
        },
      })
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error updating company invoice check delay',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * Custom hook for updating a company's invoice check delay settings using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/set_invoice_check_delay_vadmin_company__company_id__invoice_check_delay_post
 */
export function useMutationUpdateCompanyInvoiceCheckDelay(options?: any) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      invoiceCheckDelay,
    }: UpdateCompanyInvoiceCheckDelayArguments) =>
      updateCompanyInvoiceCheckDelay(companyId, invoiceCheckDelay),
    onSettled(_data, _error, { companyId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getInvoiceCheckDelay(companyId),
      });
    },
    ...options,
  });
}
